import axios from 'axios'

/**
 * Fetches user authentication status and updates authentication state.
 *
 * @function
 * @param {Object} params - The parameters for the function.
 * @param {string} params.isLocalEnvironment - The base URL for the local environment.
 * @param {Function} params.setAuthentication - Function to update authentication state.
 * @param {Object} params.authDetails - Current authentication details.
 * @returns {Promise<void>}
 */
export const getUser = async ({
  isLocalEnvironment,
  setAuthentication,
  setOrgRole,
  authDetails,
  envData,
  setActiveOrgRoutes,
  setIsDefaultOrg,
}) => {
  await fetch(isLocalEnvironment + '/auth/login/success', {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Access-Control-Allow-Credentials': true,
    },
  })
    .then((response) => {
      if (response.status === 200) return response.json()
      throw new Error('Authentication has failed!')
    })
    .then(async (resObject) => {
      // If user profile ID is present and no authentication details are set, fetch user session
      if (
        resObject.formData.user?.profile?.id &&
        !authDetails.roleId &&
        !authDetails.role &&
        !authDetails.session
      ) {
        getUserSession({
          isLocalEnvironment,
          resObject,
          setAuthentication,
          setOrgRole,
          envData,
          setActiveOrgRoutes,
          authDetails,
          setIsDefaultOrg,
        })
      }
    })
    .catch((err) => {
      console.log(err)
    })
}

/**
 * Fetches user session data and updates authentication state.
 *
 * @function
 * @param {Object} params - The parameters for the function.
 * @param {string} params.isLocalEnvironment - The base URL for the local environment.
 * @param {Object} params.resObject - The response object from the authentication check.
 * @param {Function} params.setAuthentication - Function to update authentication state.
 * @returns {Promise<void>}
 */
const getUserSession = async ({
  isLocalEnvironment,
  resObject,
  setAuthentication,
  setOrgRole,
  envData,
  setActiveOrgRoutes,
  setIsDefaultOrg,
}) => {
  const getLoginError = sessionStorage.getItem('loginError')
  if (getLoginError) {
    sessionStorage.removeItem('loginError')
  }
  await axios
    .get(
      isLocalEnvironment +
        `/authenticateSession${envData.REACT_APP_TYPE === 'builder' ? '?loginType=webAdmin' : ''}`,
      {
        method: 'GET',
        withCredentials: true,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Access-Control-Allow-Credentials': true,
        },
      },
    )
    .then(async (tres) => {
      // Extract the default user role from the session data
      let roleIsDefault
      if (envData.REACT_APP_TYPE === 'builder') {
        // if (tres.data.formData.formData) {
        //   roleIsDefault = tres.data.formData.formData.user.appAdmin.filter(
        //     (f) => f.rowFormData.roleName === 'AppAdmin',
        //   )
        // } else {
        //   roleIsDefault = tres.data.formData.user.appAdmin.filter(
        //     (f) => f.rowFormData.roleName === 'AppAdmin',
        //   )
        // }
        roleIsDefault = [{ ...tres.data.formData.user.appAdmin }]
      } else {
        roleIsDefault = tres.data.formData.formData.user.userRoles.filter(
          (f) => f.UserRole.isDefault === 1,
        )
      }
      await setAuthentication({
        resObject: resObject.formData,
        tokenSource: 'passport',
        session: tres.data.formData.formData || tres.data.formData,
        role: roleIsDefault[0]?.roleName || roleIsDefault[0]?.rowFormData?.roleName,
        roleId: roleIsDefault[0]?.roleId || roleIsDefault[0]?.rowFormData?.roleId,
      })
      const defaultOrg = JSON.parse(tres.data.formData.formData.user.userSetting.userSettings)
      if (tres.data.formData.org) {
        setDefaultAppLocal(
          setActiveOrgRoutes,
          isLocalEnvironment,
          setOrgRole,
          resObject.formData.tokenObject,
          setIsDefaultOrg,
          defaultOrg,
          tres.data.formData.org,
        )
      } else if (
        defaultOrg.orgDetails &&
        defaultOrg.orgDetails.tenantId &&
        defaultOrg.orgDetails.appId &&
        defaultOrg.orgDetails.orgId &&
        envData.REACT_APP_TYPE === 'app'
      ) {
        setDefaultApp(
          defaultOrg.orgDetails,
          setActiveOrgRoutes,
          isLocalEnvironment,
          setOrgRole,
          resObject.formData.tokenObject,
          setIsDefaultOrg,
          defaultOrg,
          tres.data.formData.org,
        )
      }
    })
    .catch((err) => {
      sessionStorage.setItem('loginError', true)
      console.log(err)
    })
}

const setDefaultAppLocal = async (
  setActiveOrgRoutes,
  isLocalEnvironment,
  setOrgRole,
  tokenObject,
  setIsDefaultOrg,
  defaultOrg,
  org,
) => {
  sessionStorage.removeItem('defApp')
  sessionStorage.setItem('defAppType', org.orgType)
  sessionStorage.setItem('defApp', JSON.stringify(org))

  await axios
    .get(
      isLocalEnvironment +
        `/getData?endPoint=orgAppData&targetTenantId=${org.tenantId}&targetAppId=${org.appId}&targetOrgId=${org.orgId}`,
      {
        method: 'GET',
        withCredentials: true,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Access-Control-Allow-Credentials': true,
          tokenSource: 'passport',
          authorization: `Bearer ${tokenObject}`,
        },
      },
    )
    .then((resp) => {
      sessionStorage.setItem(
        'activeRoutes',
        JSON.stringify({
          formData: { Pages: resp.data.formData.pageData, orgRoles: resp.data.formData.userRoles },
        }),
      )
      setActiveOrgRoutes({
        formData: { Pages: resp.data.formData.pageData, orgRoles: resp.data.formData.userRoles },
      })
      setIsDefaultOrg(defaultOrg)
      const orgRoleDetails = resp.data.formData?.userRoles?.filter((f) => f.isDefault === 1)
      setOrgRole(orgRoleDetails[0].rowFormData.roleName, orgRoleDetails[0].rowFormData.roleId)
    })
}

const setDefaultApp = async (
  app,
  setActiveOrgRoutes,
  isLocalEnvironment,
  setOrgRole,
  tokenObject,
  setIsDefaultOrg,
  defaultOrg,
) => {
  sessionStorage.removeItem('defApp')
  sessionStorage.setItem('defApp', JSON.stringify(app))

  await axios
    .get(
      isLocalEnvironment +
        `/getData?endPoint=orgAppData&targetTenantId=${app.tenantId}&targetAppId=${app.appId}&targetOrgId=${app.orgId}`,
      {
        method: 'GET',
        withCredentials: true,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Access-Control-Allow-Credentials': true,
          tokenSource: 'passport',
          authorization: `Bearer ${tokenObject}`,
        },
      },
    )
    .then((resp) => {
      sessionStorage.setItem(
        'activeRoutes',
        JSON.stringify({
          formData: { Pages: resp.data.formData.pageData, orgRoles: resp.data.formData.userRoles },
        }),
      )
      setActiveOrgRoutes({
        formData: { Pages: resp.data.formData.pageData, orgRoles: resp.data.formData.userRoles },
      })
      setIsDefaultOrg(defaultOrg)
      const orgRoleDetails = resp.data.formData?.userRoles?.filter((f) => f.isDefault === 1)
      setOrgRole(orgRoleDetails[0].rowFormData.roleName, orgRoleDetails[0].rowFormData.roleId)
    })
}
